import { Button, Group, Stack, Text } from "@mantine/core";
import { IDocxTemplate } from "@mctypes/global.types";
import { IMeeting } from "@mctypes/meeting.types";
import { IconSend } from "@tabler/icons-react";
import Axios from "axios";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { apiSlice } from "../../../../apiSlice";
import { DocxReport, FailedAlert, MCLoader } from "../../../../components";
import DocxReportDropdown from "../../../../components/DocxReportDropdown/DocxReportDropdown";
import GlobalFileUploaderTable from "../../../../components/FileUploader/GlobalFileUploaderTable";
import { setShowSuccessMessageThunk } from "../../../../global/globalSlice";
import { useGetOfficalMulticonsultMeetingTempaltesQuery } from "../../../../globalRTK";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { ToastMessagesEnums } from "../../../../mc-shared/sharedConstants";
import { useDeleteMeetingDocxTemplateMutation, useGetProjectMeetingDocxTemplatesQuery } from "../../../projectRTK";
import { getProjectIdFromUrl } from "../../../utils";
import { downloadAzureBlobFile } from "../../admin/restQueries";
import { doGenerateMeetingDocx } from "../meetingAPIDepricated";
import { MEETING_DOCX_DEMO_DATA } from "../meetingConstants";
import { setSelectedDocxTemplateInRedux } from "../meetingSlice";
import SendMeetingAgendaByMailModal from "./Agenda/SendMeetingAgendaByMailModal";
import ConfirmGenerateDocxModal from "./ConfirmGenerateDocxModal";

export const MeetingDocx: React.FC<{ singleMeeting: IMeeting.MeetingPopulated }> = ({ singleMeeting }) => {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [docxModalOpen, setDocxModalOpen] = useState<boolean>(false);
  const [sendAgendaMailModalOpen, setSendAgendaMailModalOpen] = useState<boolean>(false);
  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);
  const meetingSerie = singleMeeting?.meetingSerie as IMeeting.MeetingSerie;
  const dispatch = useAppDispatch();

  const excludeFinishedTasks = useAppSelector((state) => state.meetingReducer.excludeFinishedTasks);
  const selectedTemplate = useAppSelector((state) => state.meetingReducer.selectedDocxTemplate);

  const {
    data: officialMeetingTemplates = [],
    isLoading: isLoadingMeetingTemplates,
    isError: getMeetingTemplatesError,
  } = useGetOfficalMulticonsultMeetingTempaltesQuery("");

  const {
    data: projectMeetingDocxTemplates = [],
    isLoading: isLoadingProjectMeetingTemplates,
    isError: getProjectMeetingTemplatesError,
  } = useGetProjectMeetingDocxTemplatesQuery({ projectId });

  const [deleteTemplate, { isError: deleteTemplateError }] = useDeleteMeetingDocxTemplateMutation();

  const officalDocxTemplatesFormatted: IDocxTemplate[] = officialMeetingTemplates.map((template) => {
    const docxTemplate: IDocxTemplate = {
      _id: template.blobName,
      fileName: template.fileName,
      isMulticonsult: true,
      uploadedDate: new Date(template.uploadedAt),
      uploadedBy: {
        name: template.uploadedBy,
      },
    };
    return docxTemplate;
  });

  const projectDocxTemplatesFormatted: IDocxTemplate[] = projectMeetingDocxTemplates.map((template) => {
    const docxTemplate: IDocxTemplate = {
      _id: template.blobName,
      fileName: template.fileName,
      isMulticonsult: false,
      uploadedDate: new Date(template.uploadedAt),
      uploadedBy: {
        name: template.uploadedBy,
      },
    };
    return docxTemplate;
  });

  const docxTemplates = [...officalDocxTemplatesFormatted, ...projectDocxTemplatesFormatted];

  const generateMeetingDocx = async (docxTemplate) => {
    const selectedDocxTemplate = [...officialMeetingTemplates, ...projectMeetingDocxTemplates].find(
      (tmpl) => tmpl.fileName === docxTemplate.fileName,
    );

    await doGenerateMeetingDocx(
      projectId,
      singleMeeting._id,
      singleMeeting.name,
      selectedDocxTemplate.blobName,
      meetingSerie?.name,
      singleMeeting.dateOfMeeting,
      excludeFinishedTasks,
    );
    dispatch(apiSlice.util.invalidateTags(["Meeting", "AzureBlobStorage"]));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
  };

  const uploadDocxTempalte = async (event) => {
    await doUploadMeetingTemplateForProject(projectId, event);
    dispatch(apiSlice.util.invalidateTags(["Meeting"]));
  };

  const isLoading = isLoadingMeetingTemplates || isLoadingProjectMeetingTemplates;
  const isError = getMeetingTemplatesError || getProjectMeetingTemplatesError || deleteTemplateError;

  const handleSelectTemplate = (docxTemplate: IDocxTemplate) => {
    dispatch(setSelectedDocxTemplateInRedux(docxTemplate));
    setConfirmModalOpen(true);
  };

  const handleConfirmGenerate = async () => {
    if (selectedTemplate) {
      await generateMeetingDocx(selectedTemplate);
    }
    setConfirmModalOpen(false);
    dispatch(setSelectedDocxTemplateInRedux(null));
  };

  const handleCancelGenerate = () => {
    setConfirmModalOpen(false);
    dispatch(setSelectedDocxTemplateInRedux(null));
  };

  return (
    <Stack p="md" gap="md">
      {isLoading === true && <MCLoader />}
      {isError === true && <FailedAlert />}
      <Group justify="space-between" align="flex-start">
        <DocxReportDropdown
          docxTemplates={docxTemplates}
          generateDocx={handleSelectTemplate}
          openDocxModal={() => setDocxModalOpen(true)}
        />
        {docxModalOpen === true && (
          <DocxReport
            demoDocxData={MEETING_DOCX_DEMO_DATA}
            docxTemplates={docxTemplates}
            uploadCompleedCb={() => dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPLOAD_SUCCESFULL))}
            uploadFunc={uploadDocxTempalte}
            generateDocx={generateMeetingDocx}
            downloadTemplate={(docxTemplate) => downloadAzureBlobFile(docxTemplate._id, docxTemplate.fileName)}
            deleteDocx={(docxTemplate: IDocxTemplate) =>
              deleteTemplate({
                projectId: projectId,
                fullBlobId: docxTemplate._id,
              })
            }
            closeModal={() => setDocxModalOpen(false)}
          />
        )}
        <Button
          variant="outline"
          size="sm"
          leftSection={<IconSend size={16} />}
          onClick={() => setSendAgendaMailModalOpen(true)}
        >
          Send møtereferat per mail
        </Button>
        {sendAgendaMailModalOpen === true && (
          <SendMeetingAgendaByMailModal
            meeting={singleMeeting}
            altTitle={"møtereferat"}
            close={() => setSendAgendaMailModalOpen(false)}
          />
        )}
      </Group>
      <ConfirmGenerateDocxModal
        onConfirm={handleConfirmGenerate}
        onCancel={handleCancelGenerate}
        opened={confirmModalOpen}
      />
      <Text fw={400} fz="lg">
        Møtereferater
      </Text>
      <GlobalFileUploaderTable path={`${projectId}/meeting/${singleMeeting._id}/referater`} disableUploader={true} />
      <Text fw={400} fz="lg" mt={24}>
        Opplastede filer
      </Text>
      <GlobalFileUploaderTable path={`${projectId}/meeting/${singleMeeting._id}/files`} />
    </Stack>
  );
};

const doUploadMeetingTemplateForProject = async (projectId: string, event: React.ChangeEvent<HTMLInputElement>) => {
  try {
    const data = new FormData();
    data.append("fileToUpload", event.target.files[0]);
    const resp = await Axios.request({
      method: "POST",
      url: `/api/project/${projectId}/meetingdocxtemplate`,
      data,
      headers: {
        "content-type": `multipart/form-data`,
      },
    });
    return Promise.resolve(resp);
  } catch (error) {
    return Promise.reject({ type: "backend", message: error.response?.data });
  }
};
